import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { vehicleDetailAPI } from '../services/api';
import './VehicleDetailForm.css';

// Lấy base URL từ environment hoặc dùng production URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://apiqldangkyxe.nguyenluan.vn';

const VehicleDetailForm = ({ vehicle, onSave, onReset, onClose }) => {
  const [formData, setFormData] = useState({
    // Thông tin chủ sở hữu
    ownerName: vehicle.ownerName || '',
    ownerBirthYear: vehicle.ownerBirthYear || '',
    ownerPhone: vehicle.ownerPhone || '',
    ownerAddress: vehicle.ownerAddress || '',
    
    // Thông tin người đang sử dụng
    currentUserName: vehicle.currentUserName || '',
    currentUserBirthYear: vehicle.currentUserBirthYear || '',
    currentUserPhone: vehicle.currentUserPhone || '',
    currentUserAddress: vehicle.currentUserAddress || '',
    
    // Thông tin người điều khiển
    driverName: vehicle.driverName || '',
    driverBirthYear: vehicle.driverBirthYear || '',
    driverLicenseClass: vehicle.driverLicenseClass || '',
    driverLicenseNumber: vehicle.driverLicenseNumber || '',
    driverPhone: vehicle.driverPhone || '',
    driverAddress: vehicle.driverAddress || '',
    
    // Thông tin kỹ thuật
    isModified: vehicle.isModified || false,
    isExtended: vehicle.isExtended || false,
    plateColor: vehicle.plateColor || '',
    platePosition: vehicle.platePosition || '',
    hasModifiedLights: vehicle.hasModifiedLights || false,
    usageExpiry: vehicle.usageExpiry || '',
    
    // Thiết bị giám sát
    hasMonitoringDevice: vehicle.hasMonitoringDevice || false,
    monitoringAccount: vehicle.monitoringAccount || '',
    monitoringPassword: vehicle.monitoringPassword || '',
    monitoringProvider: vehicle.monitoringProvider || '',
    
    // Cán bộ thu thập
    collectorName: vehicle.collectorName || '',
    collectorUnit: vehicle.collectorUnit || '',
    hasPhoto: vehicle.hasPhoto || false,
    
    notes: vehicle.notes || ''
  });

  const [errors, setErrors] = useState({});
  const [photos, setPhotos] = useState([]); // Danh sách ảnh đã có
  const [newPhotoFiles, setNewPhotoFiles] = useState([]); // Ảnh mới chọn để upload
  const [newPhotoPreviews, setNewPhotoPreviews] = useState([]); // Preview ảnh mới
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null); // Ảnh đang xem

  // Load ảnh khi mở form
  useEffect(() => {
    if (vehicle.id) {
      fetchPhotos();
    }
  }, [vehicle.id]);

  const fetchPhotos = async () => {
    try {
      setLoadingPhotos(true);
      const response = await vehicleDetailAPI.getVehiclePhotos(vehicle.id);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Xóa lỗi khi người dùng bắt đầu nhập
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Thông tin chủ sở hữu - bắt buộc
    if (!formData.ownerName?.trim()) newErrors.ownerName = 'Vui lòng nhập tên chủ sở hữu';
    if (!formData.ownerBirthYear) newErrors.ownerBirthYear = 'Vui lòng nhập năm sinh';
    if (!formData.ownerPhone?.trim()) newErrors.ownerPhone = 'Vui lòng nhập số điện thoại';
    if (!formData.ownerAddress?.trim()) newErrors.ownerAddress = 'Vui lòng nhập địa chỉ';
    
    // Thông tin người đang sử dụng - bắt buộc
    if (!formData.currentUserName?.trim()) newErrors.currentUserName = 'Vui lòng nhập tên người sử dụng';
    if (!formData.currentUserBirthYear) newErrors.currentUserBirthYear = 'Vui lòng nhập năm sinh';
    if (!formData.currentUserPhone?.trim()) newErrors.currentUserPhone = 'Vui lòng nhập số điện thoại';
    if (!formData.currentUserAddress?.trim()) newErrors.currentUserAddress = 'Vui lòng nhập địa chỉ';
    
    // Thông tin người điều khiển - bắt buộc
    if (!formData.driverName?.trim()) newErrors.driverName = 'Vui lòng nhập tên người điều khiển';
    if (!formData.driverBirthYear) newErrors.driverBirthYear = 'Vui lòng nhập năm sinh';
    if (!formData.driverLicenseClass?.trim()) newErrors.driverLicenseClass = 'Vui lòng nhập hạng GPLX';
    if (!formData.driverLicenseNumber?.trim()) newErrors.driverLicenseNumber = 'Vui lòng nhập số GPLX';
    if (!formData.driverPhone?.trim()) newErrors.driverPhone = 'Vui lòng nhập số điện thoại';
    if (!formData.driverAddress?.trim()) newErrors.driverAddress = 'Vui lòng nhập địa chỉ';
    
    // Thông tin kỹ thuật - bắt buộc
    if (!formData.plateColor?.trim()) newErrors.plateColor = 'Vui lòng nhập màu biển';
    if (!formData.platePosition?.trim()) newErrors.platePosition = 'Vui lòng nhập vị trí lắp biển';
    if (!formData.usageExpiry?.trim()) newErrors.usageExpiry = 'Vui lòng nhập niên hạn sử dụng';
    
    // Thiết bị giám sát - nếu có thiết bị thì bắt buộc nhập đầy đủ
    if (formData.hasMonitoringDevice) {
      if (!formData.monitoringAccount?.trim()) newErrors.monitoringAccount = 'Vui lòng nhập tài khoản';
      if (!formData.monitoringPassword?.trim()) newErrors.monitoringPassword = 'Vui lòng nhập mật khẩu';
      if (!formData.monitoringProvider?.trim()) newErrors.monitoringProvider = 'Vui lòng nhập đơn vị cung cấp';
    }
    
    // Cán bộ thu thập - bắt buộc
    if (!formData.collectorName?.trim()) newErrors.collectorName = 'Vui lòng nhập họ tên cán bộ';
    if (!formData.collectorUnit?.trim()) newErrors.collectorUnit = 'Vui lòng nhập đơn vị công tác';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Vui lòng nhập đầy đủ các trường thông tin bắt buộc!', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    try {
      // 1. Lưu thông tin phương tiện
      await vehicleDetailAPI.updateVehicleDetail(vehicle.id, formData);
      
      // 2. Upload ảnh mới nếu có
      if (newPhotoFiles.length > 0) {
        setUploadingPhotos(true);
        const formData = new FormData();
        newPhotoFiles.forEach(file => {
          formData.append('photos', file);
        });
        await vehicleDetailAPI.uploadVehiclePhotos(vehicle.id, formData);
        setNewPhotoFiles([]);
        setNewPhotoPreviews([]);
      }
      
      toast.success('Lưu thông tin thành công!', {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Reload ảnh và đóng modal
      await fetchPhotos();
      onSave(formData);
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error('Lỗi khi lưu thông tin!', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    // Kiểm tra số lượng (max 10 ảnh mới cùng lúc)
    if (files.length > 10) {
      toast.error('Chỉ được chọn tối đa 10 ảnh cùng lúc!');
      return;
    }
    
    // Kiểm tra tổng số ảnh (hiện tại + mới)
    if (photos.length + newPhotoFiles.length + files.length > 10) {
      toast.error(`Mỗi phương tiện chỉ được có tối đa 10 ảnh! (Hiện có ${photos.length + newPhotoFiles.length} ảnh)`);
      return;
    }
    
    // Kiểm tra từng file
    const validFiles = [];
    const previews = [];
    
    for (const file of files) {
      // Kiểm tra loại file
      if (!file.type.startsWith('image/')) {
        toast.error(`File ${file.name} không phải là ảnh!`);
        continue;
      }
      // Kiểm tra kích thước (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} vượt quá 10MB!`);
        continue;
      }
      
      validFiles.push(file);
      
      // Tạo preview
      const reader = new FileReader();
      reader.onloadend = () => {
        previews.push(reader.result);
        if (previews.length === validFiles.length) {
          setNewPhotoPreviews(prev => [...prev, ...previews]);
        }
      };
      reader.readAsDataURL(file);
    }
    
    if (validFiles.length > 0) {
      setNewPhotoFiles(prev => [...prev, ...validFiles]);
      handleChange('hasPhoto', true);
    }
  };

  const handleDeleteExistingPhoto = async (photoId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Xác nhận xóa',
      text: 'Bạn có chắc muốn xóa ảnh này?',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Xóa',
      cancelButtonText: 'Hủy'
    });

    if (!result.isConfirmed) return;

    try {
      setDeletingPhotoId(photoId);
      await vehicleDetailAPI.deleteVehiclePhoto(vehicle.id, photoId);
      await fetchPhotos();
      toast.success('Đã xóa ảnh thành công!');
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Lỗi khi xóa ảnh!');
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const handleRemoveNewPhoto = (index) => {
    setNewPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setNewPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleViewPhoto = (photoUrl) => {
    setViewingPhoto(photoUrl);
  };

  const handleClosePhotoViewer = () => {
    setViewingPhoto(null);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <form className="modal-content vehicle-detail-form" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div className="modal-header">
          <h3>Thông tin chi tiết phương tiện: {vehicle.licensePlate}</h3>
          <button className="btn-close" onClick={onClose} type="button">✕</button>
        </div>

        <div className="form-scroll"  onClick={(e) => e.stopPropagation()}>
            {/* Thông tin chủ sở hữu */}
            <div className="form-section">
              <h4>👤 Thông tin chủ sở hữu</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Tên chủ sở hữu: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.ownerName}
                    onChange={(e) => handleChange('ownerName', e.target.value)}
                    placeholder="Nhập tên chủ sở hữu"
                    className={errors.ownerName ? 'error' : ''}
                  />
                  <span className="error-message">{errors.ownerName || ''}</span>
                </div>
                <div className="form-group">
                  <label>Năm sinh: <span className="required">*</span></label>
                  <input
                    type="number"
                    value={formData.ownerBirthYear}
                    onChange={(e) => handleChange('ownerBirthYear', e.target.value)}
                    min="1900"
                    max="2100"
                    placeholder="VD: 1990"
                    className={errors.ownerBirthYear ? 'error' : ''}
                  />
                  <span className="error-message">{errors.ownerBirthYear || ''}</span>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Số điện thoại: <span className="required">*</span></label>
                  <input
                    type="tel"
                    value={formData.ownerPhone}
                    onChange={(e) => handleChange('ownerPhone', e.target.value)}
                    placeholder="VD: 0912345678"
                    className={errors.ownerPhone ? 'error' : ''}
                  />
                  <span className="error-message">{errors.ownerPhone || ''}</span>
                </div>
                <div className="form-group">
                  <label>Địa chỉ: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.ownerAddress}
                    onChange={(e) => handleChange('ownerAddress', e.target.value)}
                    placeholder="Nhập địa chỉ"
                    className={errors.ownerAddress ? 'error' : ''}
                  />
                  <span className="error-message">{errors.ownerAddress || ''}</span>
                </div>
              </div>
            </div>

            {/* Thông tin người đang sử dụng */}
            <div className="form-section">
              <h4>🚗 Thông tin người đang sử dụng phương tiện</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Tên người sử dụng: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.currentUserName}
                    onChange={(e) => handleChange('currentUserName', e.target.value)}
                    placeholder="Nhập tên người sử dụng"
                    className={errors.currentUserName ? 'error' : ''}
                  />
                  <span className="error-message">{errors.currentUserName || ''}</span>
                </div>
                <div className="form-group">
                  <label>Năm sinh: <span className="required">*</span></label>
                  <input
                    type="number"
                    value={formData.currentUserBirthYear}
                    onChange={(e) => handleChange('currentUserBirthYear', e.target.value)}
                    min="1900"
                    max="2100"
                    placeholder="VD: 1990"
                    className={errors.currentUserBirthYear ? 'error' : ''}
                  />
                  <span className="error-message">{errors.currentUserBirthYear || ''}</span>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Số điện thoại: <span className="required">*</span></label>
                  <input
                    type="tel"
                    value={formData.currentUserPhone}
                    onChange={(e) => handleChange('currentUserPhone', e.target.value)}
                    placeholder="VD: 0912345678"
                    className={errors.currentUserPhone ? 'error' : ''}
                  />
                  <span className="error-message">{errors.currentUserPhone || ''}</span>
                </div>
                <div className="form-group">
                  <label>Địa chỉ: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.currentUserAddress}
                    onChange={(e) => handleChange('currentUserAddress', e.target.value)}
                    placeholder="Nhập địa chỉ"
                    className={errors.currentUserAddress ? 'error' : ''}
                  />
                  <span className="error-message">{errors.currentUserAddress || ''}</span>
                </div>
              </div>
            </div>

            {/* Thông tin người điều khiển */}
            <div className="form-section">
              <h4>🪪 Thông tin người điều khiển phương tiện</h4>
              <div className="form-row three-cols">
                <div className="form-group">
                  <label>Tên người điều khiển: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.driverName}
                    onChange={(e) => handleChange('driverName', e.target.value)}
                    placeholder="Nhập tên người điều khiển"
                    className={errors.driverName ? 'error' : ''}
                  />
                  <span className="error-message">{errors.driverName || ''}</span>
                </div>
                <div className="form-group">
                  <label>Năm sinh: <span className="required">*</span></label>
                  <input
                    type="number"
                    value={formData.driverBirthYear}
                    onChange={(e) => handleChange('driverBirthYear', e.target.value)}
                    min="1900"
                    max="2100"
                    placeholder="VD: 1990"
                    className={errors.driverBirthYear ? 'error' : ''}
                  />
                  <span className="error-message">{errors.driverBirthYear || ''}</span>
                </div>
                <div className="form-group">
                  <label>Số điện thoại: <span className="required">*</span></label>
                  <input
                    type="tel"
                    value={formData.driverPhone}
                    onChange={(e) => handleChange('driverPhone', e.target.value)}
                    placeholder="VD: 0912345678"
                    className={errors.driverPhone ? 'error' : ''}
                  />
                  <span className="error-message">{errors.driverPhone || ''}</span>
                </div>
              </div>
              <div className="form-row three-cols">
                <div className="form-group">
                  <label>Hạng GPLX: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.driverLicenseClass}
                    onChange={(e) => handleChange('driverLicenseClass', e.target.value)}
                    placeholder="A1, A2, B1, B2..."
                    className={errors.driverLicenseClass ? 'error' : ''}
                  />
                  <span className="error-message">{errors.driverLicenseClass || ''}</span>
                </div>
                <div className="form-group">
                  <label>Số GPLX: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.driverLicenseNumber}
                    onChange={(e) => handleChange('driverLicenseNumber', e.target.value)}
                    placeholder="Nhập số GPLX"
                    className={errors.driverLicenseNumber ? 'error' : ''}
                  />
                  <span className="error-message">{errors.driverLicenseNumber || ''}</span>
                </div>
                <div className="form-group">
                  <label>Địa chỉ: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.driverAddress}
                    onChange={(e) => handleChange('driverAddress', e.target.value)}
                    placeholder="Nhập địa chỉ"
                    className={errors.driverAddress ? 'error' : ''}
                  />
                  <span className="error-message">{errors.driverAddress || ''}</span>
                </div>
              </div>
            </div>

            {/* Thông tin kỹ thuật */}
            <div className="form-section">
              <h4>🔧 Thông tin kỹ thuật phương tiện</h4>
              <div className="form-row three-cols">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isModified}
                      onChange={(e) => handleChange('isModified', e.target.checked)}
                    />
                    Hoán cải phương tiện
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isExtended}
                      onChange={(e) => handleChange('isExtended', e.target.checked)}
                    />
                    Cơi nới thành thùng
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.hasModifiedLights}
                      onChange={(e) => handleChange('hasModifiedLights', e.target.checked)}
                    />
                    Độ chế còi đèn
                  </label>
                </div>
              </div>
              <div className="form-row three-cols">
                <div className="form-group">
                  <label>Màu biển: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.plateColor}
                    onChange={(e) => handleChange('plateColor', e.target.value)}
                    placeholder="Trắng, Vàng, Xanh..."
                    className={errors.plateColor ? 'error' : ''}
                  />
                  <span className="error-message">{errors.plateColor || ''}</span>
                </div>
                <div className="form-group">
                  <label>Vị trí lắp biển số: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.platePosition}
                    onChange={(e) => handleChange('platePosition', e.target.value)}
                    placeholder="Nhập vị trí"
                    className={errors.platePosition ? 'error' : ''}
                  />
                  <span className="error-message">{errors.platePosition || ''}</span>
                </div>
                <div className="form-group">
                  <label>Niên hạn sử dụng: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.usageExpiry}
                    onChange={(e) => handleChange('usageExpiry', e.target.value)}
                    placeholder="VD: 2025-12-31"
                    className={errors.usageExpiry ? 'error' : ''}
                  />
                  <span className="error-message">{errors.usageExpiry || ''}</span>
                </div>
              </div>
            </div>

            {/* Thiết bị giám sát */}
            <div className="form-section">
              <h4>📹 Thiết bị giám sát hành trình và ghi nhận hình ảnh</h4>
              <div className="form-row full-width">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.hasMonitoringDevice}
                      onChange={(e) => handleChange('hasMonitoringDevice', e.target.checked)}
                    />
                    Có thiết bị giám sát
                  </label>
                </div>
              </div>
              
              {formData.hasMonitoringDevice && (
                <div className="monitoring-details">
                  <div className="form-row three-cols">
                    <div className="form-group">
                      <label>Tài khoản: <span className="required">*</span></label>
                      <input
                        type="text"
                        value={formData.monitoringAccount}
                        onChange={(e) => handleChange('monitoringAccount', e.target.value)}
                        placeholder="Nhập tài khoản"
                        className={errors.monitoringAccount ? 'error' : ''}
                      />
                      <span className="error-message">{errors.monitoringAccount || ''}</span>
                    </div>
                    <div className="form-group">
                      <label>Mật khẩu: <span className="required">*</span></label>
                      <input
                        type="text"
                        value={formData.monitoringPassword}
                        onChange={(e) => handleChange('monitoringPassword', e.target.value)}
                        placeholder="Nhập mật khẩu"
                        className={errors.monitoringPassword ? 'error' : ''}
                      />
                      <span className="error-message">{errors.monitoringPassword || ''}</span>
                    </div>
                    <div className="form-group">
                      <label>Đơn vị cung cấp: <span className="required">*</span></label>
                      <input
                        type="text"
                        value={formData.monitoringProvider}
                        onChange={(e) => handleChange('monitoringProvider', e.target.value)}
                        placeholder="Nhập đơn vị cung cấp"
                        className={errors.monitoringProvider ? 'error' : ''}
                      />
                      <span className="error-message">{errors.monitoringProvider || ''}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Cán bộ thu thập */}
            <div className="form-section">
              <h4>👮 Thông tin cán bộ thu thập</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Họ tên cán bộ: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.collectorName}
                    onChange={(e) => handleChange('collectorName', e.target.value)}
                    placeholder="Nhập họ tên cán bộ"
                    className={errors.collectorName ? 'error' : ''}
                  />
                  <span className="error-message">{errors.collectorName || ''}</span>
                </div>
                <div className="form-group">
                  <label>Đơn vị công tác: <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.collectorUnit}
                    onChange={(e) => handleChange('collectorUnit', e.target.value)}
                    placeholder="Nhập đơn vị công tác"
                    className={errors.collectorUnit ? 'error' : ''}
                  />
                  <span className="error-message">{errors.collectorUnit || ''}</span>
                </div>
              </div>
              
              {/* Upload ảnh phương tiện */}
              <div className="form-row full-width">
                <div className="form-group">
                  <label>📷 Ảnh phương tiện chụp cùng cán bộ kiểm tra:</label>
                  
                  {loadingPhotos ? (
                    <div className="photo-loading">Đang tải ảnh...</div>
                  ) : (
                    <div className="photo-gallery-section">
                      {/* Ảnh đã có */}
                      {photos.length > 0 && (
                        <div className="existing-photos">
                          <h5>Ảnh hiện có ({photos.length}/10):</h5>
                          <div className="photo-grid">
                            {photos.map(photo => (
                              <div key={photo.id} className="photo-item">
                                <img 
                                  src={`${API_BASE_URL}${photo.url}`} 
                                  alt="Ảnh phương tiện"
                                  onClick={() => handleViewPhoto(`${API_BASE_URL}${photo.url}`)}
                                  style={{ cursor: 'pointer' }}
                                  title="Click để xem phóng to"
                                />
                                <button
                                  type="button"
                                  className="btn-delete-photo-small"
                                  onClick={() => handleDeleteExistingPhoto(photo.id)}
                                  disabled={deletingPhotoId === photo.id}
                                  title="Xóa ảnh"
                                >
                                  {deletingPhotoId === photo.id ? '⏳' : '🗑️'}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Ảnh mới chọn (chưa upload) */}
                      {newPhotoPreviews.length > 0 && (
                        <div className="new-photos">
                          <h5>Ảnh mới chọn ({newPhotoPreviews.length}):</h5>
                          <div className="photo-grid">
                            {newPhotoPreviews.map((preview, index) => (
                              <div key={`new-${index}`} className="photo-item new">
                                <img 
                                  src={preview} 
                                  alt={`Ảnh mới ${index + 1}`}
                                  onClick={() => handleViewPhoto(preview)}
                                  style={{ cursor: 'pointer' }}
                                  title="Click để xem phóng to"
                                />
                                <button
                                  type="button"
                                  className="btn-delete-photo-small"
                                  onClick={() => handleRemoveNewPhoto(index)}
                                  title="Bỏ chọn ảnh"
                                >
                                  ✕
                                </button>
                                <span className="new-badge">Mới</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Nút thêm ảnh */}
                      {(photos.length + newPhotoFiles.length) < 10 && (
                        <div className="photo-upload-actions">
                          <label className="btn-add-photos">
                            📁 Thêm ảnh ({photos.length + newPhotoFiles.length}/10)
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handlePhotosChange}
                              style={{ display: 'none' }}
                            />
                          </label>
                          <p className="upload-hint">
                            Chọn nhiều ảnh cùng lúc (JPEG, PNG, GIF - Tối đa 10MB/ảnh, tối đa 10 ảnh)
                          </p>
                        </div>
                      )}
                      
                      {(photos.length + newPhotoFiles.length) >= 10 && (
                        <p className="upload-limit-reached">
                          ⚠️ Đã đạt giới hạn 10 ảnh. Xóa ảnh cũ để thêm ảnh mới.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Ghi chú */}
            <div className="form-section">
              <h4>📝 Ghi chú</h4>
              <div className="form-row full-width">
                <div className="form-group">
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows="3"
                    placeholder="Ghi chú thêm..."
                  />
                </div>
              </div>
            </div>
        </div>

        <div className="vdf-footer-actions">
          <button type="button" className="vdf-btn vdf-btn-reset" onClick={onReset} title="Làm mới lại toàn bộ dữ liệu phương tiện">
            <span className="vdf-btn-icon">🔄</span>
            <span>Làm mới</span>
          </button>
          <button type="button" className="vdf-btn vdf-btn-cancel" onClick={onClose}>
            <span>Hủy</span>
          </button>
          <button type="submit" className="vdf-btn vdf-btn-submit" disabled={uploadingPhotos}>
            <span className="vdf-btn-icon">{uploadingPhotos ? '⏳' : '💾'}</span>
            <span>{uploadingPhotos ? 'Đang lưu...' : 'Lưu thông tin'}</span>
          </button>
        </div>
      </form>
      </div>

      {/* Photo Viewer Modal - Tách ra ngoài để không bị conflict với form modal */}
      {viewingPhoto && (
        <div className="photo-viewer-overlay" onClick={handleClosePhotoViewer}>
          <div className="photo-viewer-content" onClick={(e) => e.stopPropagation()}>
            <button className="photo-viewer-close" onClick={handleClosePhotoViewer}>
              ✕
            </button>
            <img src={viewingPhoto} alt="Xem ảnh phóng to" />
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleDetailForm;

